import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { useCanvasStore } from '../store/canvasStore';

export function useFileHandling() {
  const logoInputRef = useCanvasStore((s) => s.logoInputRef);
  const fileInputRef = useCanvasStore((s) => s.fileInputRef);
  const setBgImage = useCanvasStore((s) => s.setBgImage);
  const saveHistory = useCanvasStore((s) => s.saveStateToHistory);
  const addImage = useCanvasStore((s) => s.addImage);

  const validateFile = (file: File): boolean => {
    const validMimeTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    const maxSize = 5 * 1024 * 1024; // 5MB limit

    if (!validMimeTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload an image (JPG, PNG, GIF, WebP, or SVG)');
      return false;
    }

    if (file.size > maxSize) {
      toast.error('File too large. Maximum size is 5MB');
      return false;
    }

    return true;
  };

  const handleBgUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files?.length) return;

      const file = files[0];
      if (!validateFile(file)) {
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      saveHistory();

      try {
        const reader = new FileReader();
        reader.onload = (event) => {
          if (typeof event.target?.result === 'string') {
            const sanitizedDataUrl = event.target.result.replace(/^data:image\/[^;]+;base64,/, 'data:image/;base64,');
            setBgImage(sanitizedDataUrl);
          }
        };

        reader.onerror = () => toast.error('Failed to read file');
        reader.readAsDataURL(file);

        if (fileInputRef.current) fileInputRef.current.value = '';
      } catch (err) {
        toast.error('Failed to upload background image');
      }
    },
    [saveHistory, setBgImage, fileInputRef]
  );

  const handleLogoUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || !files.length) return;

      saveHistory();

      try {
        Array.from(files).forEach((file) => {
          if (!validateFile(file)) return;

          const reader = new FileReader();

          reader.onload = (event) => {
            if (typeof event.target?.result === 'string') {
              addImage(event.target.result);
            }
          };

          reader.readAsDataURL(file);
        });

        if (logoInputRef.current) {
          logoInputRef.current.value = '';
        }
      } catch (err) {
        toast.error('Failed to upload image(s)');
      }
    },
    [saveHistory, addImage, logoInputRef]
  );

  return { handleBgUpload, handleLogoUpload };
}
